import { useQueryClient } from "@tanstack/react-query";
import { ServerCrash } from "lucide-react";
import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router";

import { markSignedOut, sessionQuery, useIsAdmin, useSession } from "../../api/auth";
import { errorMessage, onSignedOut } from "../../api/client";
import { GridBackdrop } from "../../ui/backdrop/GridBackdrop";
import { Button } from "../../ui/Button";
import { Spinner } from "../../ui/Spinner";
import styles from "./Auth.module.css";

/**
 * Every page of the app needs a signed-in session. Without one the visitor goes to the sign-in
 * page, which brings them back to the page they asked for. The server refuses every request
 * without a session anyway; this only decides what the screen shows.
 */
export function RequireAuth() {
  const session = useSession();
  const client = useQueryClient();
  const location = useLocation();

  useEffect(
    () =>
      onSignedOut(() => {
        // A request found the session gone: ended on the server, or signed out in another tab.
        if (client.getQueryData(sessionQuery.queryKey)?.status === "signed-in") markSignedOut(client, "ended");
      }),
    [client],
  );

  if (session.data?.status === "signed-in") return <Outlet />;
  if (session.data?.status === "signed-out") {
    const { reason } = session.data;
    const here = `${location.pathname}${location.search}${location.hash}`;
    // After Sign out the next sign-in starts at the chat; otherwise it comes back here.
    const to = reason === "signed-out" || here === "/" ? "/login" : `/login?next=${encodeURIComponent(here)}`;
    return <Navigate to={to} replace state={{ reason }} />;
  }
  if (session.isError) {
    return (
      <main className={styles.boot}>
        <GridBackdrop />
        <div className={styles.bootCard} role="alert">
          <ServerCrash size={36} aria-hidden="true" />
          <h1 className={styles.bootTitle}>Can't reach QwikSupport</h1>
          <p className={styles.bootText}>{errorMessage(session.error)}</p>
          <Button variant="primary" loading={session.isFetching} onClick={() => void session.refetch()}>
            Try again
          </Button>
        </div>
      </main>
    );
  }
  return (
    <main className={styles.boot} aria-busy="true">
      <GridBackdrop />
      <span className={styles.bootSpinner}>
        <Spinner size={22} />
        <span className="visually-hidden">Loading</span>
      </span>
    </main>
  );
}

/**
 * The pages for the admin only: Dashboard, Settings and what leads there. An account with the
 * user role has the Chat page alone, so it is sent there. The server refuses these pages' requests
 * to such an account anyway.
 */
export function RequireAdmin() {
  return useIsAdmin() ? <Outlet /> : <Navigate to="/chat" replace />;
}
